import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDoNotDisturbOn } from "react-icons/md";
import TooltippedElement from "components/TooltippedElement";
import type { CampaignType, Recipient } from "../types/ICampaign";

interface RecipientsTableProps {
  recipients: Recipient[];
  isLoading: boolean;
  /** Drives the opted-out tooltip wording (texts vs emails). */
  campaignType?: CampaignType;
  /** When set, enables the remove/re-add UI. Only pass in the editor context. */
  excludedIds?: Set<string>;
  /** Called when the user clicks the x button on a recipient row. */
  onRemove?: (bcustomerId: string) => void;
  /** Called when the user clicks the + button in the "Removed" section. */
  onReAdd?: (bcustomerId: string) => void;
}

const RecipientsTable = ({
  recipients,
  isLoading,
  campaignType = "email",
  excludedIds,
  onRemove,
  onReAdd,
}: RecipientsTableProps) => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.filters";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const optedOutTooltip = (r: Recipient) =>
    t(
      `${tBase}.${campaignType === "sms" ? "optedOutTexts" : "optedOutEmails"}`,
      {
        date: r.optedOutAt ? new Date(r.optedOutAt).toLocaleDateString() : "",
      }
    );

  // ESERV2-1471: a suppressed address (bounce/complaint/unsubscribe block) is
  // shown the same way as an opt-out — grayed with the reason — instead of
  // silently vanishing from the preview. The count and the send skip it.
  const suppressedTooltip = (r: Recipient) =>
    t(`${tBase}.badgeUndeliverableHint`, { reason: r.suppression?.reason ?? "" });
  const blockedTooltip = (r: Recipient) =>
    r.optedOutAt ? optedOutTooltip(r) : suppressedTooltip(r);

  const [searchTerm, setSearchTerm] = useState("");
  const [showRemoved, setShowRemoved] = useState(false);

  const editable = !!excludedIds && !!onRemove && !!onReAdd;

  // Split recipients into active vs removed lists
  // Show all recipients UNLESS they have a bcustomerId that's in the excluded set.
  // Manual emails (bcustomerId is null/empty) always show — they can't be excluded.
  const activeRecipients = editable
    ? recipients.filter((r) => !r.bcustomerId || !excludedIds.has(r.bcustomerId))
    : recipients;

  const removedRecipients = editable
    ? recipients.filter((r) => r.bcustomerId && excludedIds.has(r.bcustomerId))
    : [];

  // Apply search filter to active list
  const filtered = activeRecipients.filter((r) => {
    if (!searchTerm) {
      return true;
    }
    const term = searchTerm.toLowerCase();
    const name = [r.firstName, r.lastName].filter(Boolean).join(" ").toLowerCase();
    return (
      name.includes(term) ||
      r.email?.toLowerCase().includes(term) ||
      r.phone?.toLowerCase().includes(term) ||
      r.city?.toLowerCase().includes(term) ||
      r.state?.toLowerCase().includes(term)
    );
  });

  const recipientName = (r: Recipient) =>
    [r.firstName, r.lastName].filter(Boolean).join(" ") || "-";

  return (
    <div className="space-y-4">
      <input
        type="text"
        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
        placeholder={tr("search")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {isLoading ? (
        <p className="text-gray-500">{tr("loadingRecipients")}</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500">{tr("noRecipientsFound")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 font-medium text-gray-600">{tr("name")}</th>
                <th className="px-3 py-2 font-medium text-gray-600">{tr("email")}</th>
                <th className="px-3 py-2 font-medium text-gray-600">{tr("phone")}</th>
                <th className="px-3 py-2 font-medium text-gray-600">{tr("cityState")}</th>
                {editable && (
                  <th className="px-3 py-2 font-medium text-gray-600 w-10" />
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r, idx) => {
                // Opted-out customers stay VISIBLE (grayed, with the date)
                // instead of silently disappearing — the business keeps the
                // context of why someone won't receive the campaign. They
                // are not part of the matching count and the send skips them.
                const optedOut = !!r.optedOutAt || !!r.suppression;
                return (
                  <tr
                    key={r.bcustomerId ?? `manual-${idx}`}
                    className={optedOut ? "opacity-60" : "hover:bg-gray-50"}
                  >
                    <td className="px-3 py-2 text-gray-800">
                      {optedOut ? (
                        <span className="inline-flex items-center gap-1.5">
                          <TooltippedElement
                            message={blockedTooltip(r)}
                            placement="top"
                            element={
                              <MdDoNotDisturbOn
                                className="text-red-500 text-base shrink-0"
                                aria-label={blockedTooltip(r)}
                              />
                            }
                          />
                          {recipientName(r)}
                        </span>
                      ) : (
                        recipientName(r)
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.email}</td>
                    <td className="px-3 py-2 text-gray-600">{r.phone || "-"}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {[r.city, r.state].filter(Boolean).join(", ") || "-"}
                    </td>
                    {editable && r.bcustomerId && !optedOut && (
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-500 transition-colors text-lg leading-none"
                          title="Remove from send list"
                          onClick={() => onRemove(r.bcustomerId!)}
                        >
                          &times;
                        </button>
                      </td>
                    )}
                    {editable && (!r.bcustomerId || optedOut) && <td />}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Removed recipients section */}
      {editable && removedRecipients.length > 0 && (
        <div className="mt-4 border-t pt-4">
          <button
            type="button"
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
            onClick={() => setShowRemoved(!showRemoved)}
          >
            <span className="text-xs">{showRemoved ? "▼" : "▶"}</span>
            <span>Removed ({removedRecipients.length})</span>
          </button>

          {showRemoved && (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm text-left opacity-60">
                <tbody className="divide-y divide-gray-100">
                  {removedRecipients.map((r) => (
                    <tr key={r.bcustomerId} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500 line-through">
                        {recipientName(r)}
                      </td>
                      <td className="px-3 py-2 text-gray-400 line-through">
                        {r.email}
                      </td>
                      <td className="px-3 py-2 text-gray-400">{r.phone || "-"}</td>
                      <td className="px-3 py-2 text-gray-400">
                        {[r.city, r.state].filter(Boolean).join(", ") || "-"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-green-600 transition-colors text-lg leading-none"
                          title="Re-add to send list"
                          onClick={() => onReAdd(r.bcustomerId!)}
                        >
                          +
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipientsTable;
